import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, Users, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { adminAction } from "@/lib/adminApi";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { useAdminSupport } from "@/hooks/useAdminSupport";
import { toast } from "sonner";
import {
  AdminPageHeader,
  AdminPanel,
  AdminPageSkeleton,
  AdminEmptyState,
  AdminSearch,
  AdminInput,
  AdminPill,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from "./AdminUI";

function roleTone(role: string | null): "neutral" | "accent" {
  if (!role) return "neutral";
  const n = role.toLowerCase();
  return n === "admin" || n === "owner" ? "accent" : "neutral";
}

export function AdminUsersTab() {
  const { isSuperAdmin } = useAdminSupport();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [emailDialog, setEmailDialog] = useState<{ email: string; name: string } | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = (val: string) => {
    setSearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(val), 400);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "users", debouncedSearch],
    queryFn: () => adminAction("users", { search: debouncedSearch || undefined }),
  });

  const sendEmail = useMutation({
    mutationFn: () =>
      adminAction("send_email", {
        to_email: emailDialog!.email,
        subject,
        body: sanitizeHtml(body),
      }),
    onSuccess: () => {
      toast.success("Email sent successfully");
      setEmailDialog(null);
      setSubject("");
      setBody("");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const queryClient = useQueryClient();
  const toggleInternal = useMutation({
    mutationFn: ({ id, value }: { id: string; value: boolean }) =>
      adminAction("set_internal", { target_user_id: id, value }),
    onSuccess: (_d, vars) => {
      toast.success(vars.value ? "Granted internal access" : "Revoked internal access");
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) return <AdminPageSkeleton stats={0} rows={8} columns={4} />;

  const filteredUsers = (data || []).filter((u: any) => {
    if (showTestAccounts) return true;
    const e = (u.email || "").toLowerCase();
    return !e.includes("auto-pentest.com") && !e.includes("auto-");
  });

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Customers"
        title="Users"
        description="Browse and reach out to every individual on the platform."
        meta={`${filteredUsers.length} ${filteredUsers.length === 1 ? "person" : "people"} shown`}
      />

      <div className="flex items-center gap-4">
        <AdminSearch
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Search name or email…"
          containerClassName="flex-1 max-w-md"
        />
        <label className="flex items-center gap-2 text-[12px] text-muted-foreground whitespace-nowrap cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showTestAccounts}
            onChange={(e) => setShowTestAccounts(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-border accent-primary"
          />
          Include test accounts
        </label>
      </div>

      {filteredUsers.length === 0 ? (
        <AdminEmptyState
          icon={Users}
          title="No matches"
          description="Try clearing your search or toggling test accounts."
        />
      ) : (
        <AdminPanel>
          <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            <thead style={{ borderBottom: "1px solid rgba(58,53,48,0.06)" }}>
              <tr>
                <th className={adminTableHeadCellClass}>Person</th>
                <th className={adminTableHeadCellClass}>Organisation</th>
                <th className={adminTableHeadCellClass}>Role</th>
                <th className={adminTableHeadCellClass}>Joined</th>
                <th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u: any, i: number) => (
                <tr
                  key={u.id}
                  className={adminTableRowClass}
                  style={{
                    borderTop: i === 0 ? "none" : "1px solid rgba(58,53,48,0.06)",
                  }}
                >
                  <td className={adminTableCellClass}>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(194,84,26,0.08)" }}
                      >
                        <span className="text-[12px] font-medium" style={{ color: "#c2541a" }}>
                          {(u.name || u.email || "?")[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] text-foreground truncate">
                          {u.name || "—"}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className={adminTableCellClass + " text-muted-foreground"}>
                    {u.org_name || "—"}
                  </td>
                  <td className={adminTableCellClass}>
                    {u.role ? <AdminPill tone={roleTone(u.role)}>{u.role}</AdminPill> : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className={adminTableCellClass + " text-muted-foreground tabular-nums"}>
                    {u.joined_at
                      ? new Date(u.joined_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : u.created_at
                      ? new Date(u.created_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {isSuperAdmin && u.email?.toLowerCase().endsWith("@sorvaine.com") && (
                        <label
                          className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer select-none"
                          title="Bypasses billing/plan gates"
                        >
                          <Shield className="w-3 h-3" style={{ color: u.is_internal ? "#c2541a" : undefined }} />
                          <input
                            type="checkbox"
                            checked={!!u.is_internal}
                            disabled={toggleInternal.isPending}
                            onChange={(e) => toggleInternal.mutate({ id: u.id, value: e.target.checked })}
                            className="h-3.5 w-3.5 rounded border-border accent-primary"
                          />
                          Internal
                        </label>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-[#c2541a]"
                        onClick={() => setEmailDialog({ email: u.email, name: u.name || u.email })}
                      >
                        <Mail className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminPanel>
      )}

      <Dialog open={!!emailDialog} onOpenChange={(o) => { if (!o) setEmailDialog(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Send email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">To</Label>
              <AdminInput value={emailDialog?.email || ""} readOnly className="mt-1" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Subject</Label>
              <AdminInput
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject…"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Body</Label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Email body (HTML supported)…"
                rows={6}
                className="w-full rounded-[3px] px-3 py-2 text-[13px] text-foreground placeholder:text-muted-foreground/70 mt-1 focus:outline-none focus:border-[#c2541a] focus:ring-1 focus:ring-[rgba(194,84,26,0.25)] resize-none"
                style={{ background: "#faf7f1", border: "1px solid rgba(58, 53, 48, 0.14)" }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" disabled={!subject.trim() || !body.trim()} onClick={() => setConfirmOpen(true)}>
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm send</AlertDialogTitle>
            <AlertDialogDescription>
              Send email to <strong>{emailDialog?.email}</strong>? This will be sent immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                sendEmail.mutate();
              }}
              disabled={sendEmail.isPending}
            >
              {sendEmail.isPending ? "Sending…" : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
